// ============================================================================
// AVATAR KNOWLEDGE BASE SECTION
// Manage avatar-specific knowledge base documents, search, and testing
// KB is always enabled, uses shared Qdrant collection via n8n
// ============================================================================

import React, { useState, useRef, useCallback } from 'react';
import {
  Search, Trash2, Upload, Loader2, FileText, Layers, HardDrive,
  FlaskConical, CheckCircle2, XCircle, Clock, ChevronDown, ChevronUp
} from 'lucide-react';
import { AvatarKBDocument } from '../types';
import {
  useAvatarKBDocuments,
  useAvatarKBStats,
  useUploadAvatarKBDocument,
  useDeleteAvatarKBDocument,
  useSearchAvatarKB,
  useTestAvatarKB,
  useFormatFileSize,
  useValidateAvatarKBFile,
  type AvatarKBSearchResponse,
  type AvatarKBTestResponse
} from '../hooks/useAvatarKB';

interface AvatarKBSectionProps {
  avatarDefinitionId: string;
}

// File extension badge styling
const FILE_TYPE_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  pdf: { bg: 'bg-red-50', text: 'text-red-500', border: 'border-red-100' },
  docx: { bg: 'bg-blue-50', text: 'text-blue-500', border: 'border-blue-100' },
  doc: { bg: 'bg-blue-50', text: 'text-blue-500', border: 'border-blue-100' },
  txt: { bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200' },
  md: { bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200' },
};

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string; dotAnimate?: boolean }> = {
  completed: { bg: 'bg-green-50 border-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  processing: { bg: 'bg-amber-50 border-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', dotAnimate: true },
  pending: { bg: 'bg-amber-50 border-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', dotAnimate: true },
  failed: { bg: 'bg-red-50 border-red-100', text: 'text-red-700', dot: 'bg-red-500' },
};

export default function AvatarKBSection({
  avatarDefinitionId
}: AvatarKBSectionProps) {
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AvatarKBSearchResponse | null>(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFileName, setUploadFileName] = useState('');
  const [testResults, setTestResults] = useState<AvatarKBTestResponse | null>(null);
  const [testExpanded, setTestExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hooks
  const { data: documents, isLoading, refetch } = useAvatarKBDocuments(
    avatarDefinitionId,
    { refetchInterval: 5000 }
  );
  const { data: stats } = useAvatarKBStats(avatarDefinitionId);
  const uploadMutation = useUploadAvatarKBDocument(avatarDefinitionId);
  const deleteMutation = useDeleteAvatarKBDocument(avatarDefinitionId);
  const searchMutation = useSearchAvatarKB(avatarDefinitionId);
  const testMutation = useTestAvatarKB(avatarDefinitionId);
  const formatFileSize = useFormatFileSize();
  const validateFile = useValidateAvatarKBFile();

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Upload handlers
  const handleFile = useCallback(async (file: File) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      showNotification('error', validation.error || 'Invalid file');
      return;
    }
    setIsUploading(true);
    setUploadFileName(file.name);
    try {
      await uploadMutation.mutateAsync({ file });
      showNotification('success', 'Document uploaded successfully! Processing started.');
      refetch();
      setTimeout(() => { setIsUploading(false); setUploadFileName(''); }, 2000);
    } catch (error: any) {
      showNotification('error', `Upload failed: ${error.message}`);
      setIsUploading(false);
      setUploadFileName('');
    }
  }, [uploadMutation, validateFile, refetch]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) await handleFile(files[0]);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDelete = async (documentId: string, fileName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) return;
    try {
      await deleteMutation.mutateAsync(documentId);
      showNotification('success', 'Document deleted successfully');
    } catch (error: any) {
      showNotification('error', `Delete failed: ${error.message}`);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    try {
      const results = await searchMutation.mutateAsync({
        query: searchQuery, topK: 5, includeAnswer: true
      });
      setSearchResults(results);
      if (results.sources.length === 0) showNotification('error', 'No relevant documents found');
    } catch (error: any) {
      showNotification('error', `Search failed: ${error.message}`);
    }
  };

  const handleTest = async () => {
    try {
      setTestExpanded(true);
      const results = await testMutation.mutateAsync();
      setTestResults(results);
      if (results.status === 'no_documents') {
        showNotification('error', 'No processed documents to test against');
      }
    } catch (error: any) {
      showNotification('error', `Test failed: ${error.message}`);
    }
  };

  const getFileExtension = (fileName: string): string => {
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredDocs = (documents || []).filter((doc: AvatarKBDocument) =>
    !filterQuery || doc.fileName.toLowerCase().includes(filterQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-16 right-5 px-4 py-3 rounded-xl text-sm font-medium shadow-lg z-[1000] ${
          notification.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Documents</p>
            <p className="text-2xl font-bold text-slate-900">{stats?.totalDocuments ?? documents?.length ?? 0}</p>
          </div>
          <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
            <FileText size={24} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Total Chunks</p>
            <p className="text-2xl font-bold text-slate-900">{stats?.totalChunks ?? 0}</p>
          </div>
          <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
            <Layers size={24} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Total Size</p>
            <p className="text-2xl font-bold text-slate-900">{stats ? formatFileSize(stats.totalSizeBytes) : '0 Bytes'}</p>
          </div>
          <div className="p-2 bg-green-100 rounded-lg text-green-600">
            <HardDrive size={24} />
          </div>
        </div>
      </div>

      {/* Upload + Search row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Upload zone — 1/3 */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !isUploading && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
            isDragging
              ? 'border-primary bg-primary/5 scale-[1.01]'
              : isUploading
              ? 'border-green-400 bg-green-50'
              : 'border-slate-200 bg-white hover:border-primary/50'
          }`}
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 size={28} className="text-green-500 animate-spin" />
              <p className="text-xs font-medium text-[#052831] truncate max-w-full">{uploadFileName}</p>
              <p className="text-xs font-semibold text-green-600">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload size={28} className={isDragging ? 'text-primary' : 'text-slate-400'} />
              <p className="text-sm font-semibold text-[#052831]">
                {isDragging ? 'Drop file here' : 'Upload Document'}
              </p>
              <p className="text-[10px] text-[#64748b]">Drag & drop or click to browse</p>
              <p className="text-[10px] text-[#94a3b8]">PDF, DOCX, TXT, MD (50MB max)</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,.md,.csv"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0) handleFile(files[0]);
              e.target.value = '';
            }}
            className="hidden"
          />
        </div>

        {/* Search + Test — 2/3 */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-[#e2e8f0] shadow-sm p-6 flex flex-col">
          {/* Search Section */}
          <h3 className="font-bold text-[#052831] text-base mb-3">Ask your Knowledge Base</h3>
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1 group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-slate-400 group-focus-within:text-primary transition-colors" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-[#e2e8f0] rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-[#052831] placeholder-slate-400 text-sm"
                placeholder="Ask a question about your documents..."
              />
            </div>
            <button
              type="submit"
              disabled={searchMutation.isPending || !searchQuery.trim()}
              className="px-5 py-2.5 bg-primary text-white font-bold rounded-xl hover:brightness-105 transition-all disabled:opacity-50 text-sm shrink-0"
            >
              {searchMutation.isPending ? 'Searching...' : 'Search'}
            </button>
          </form>

          {/* Search Results */}
          {searchResults && (
            <div className="mt-4 space-y-3 overflow-y-auto max-h-[200px]">
              {searchResults.answer && (
                <div className="p-3 bg-green-50/50 border-l-4 border-green-500 rounded-r-xl">
                  <h4 className="text-[10px] font-bold text-green-700 uppercase mb-1">Answer</h4>
                  <p className="text-xs text-[#052831] leading-relaxed">{searchResults.answer}</p>
                </div>
              )}
              {searchResults.sources.length > 0 && (
                <div>
                  <h4 className="text-[10px] font-bold text-[#64748b] uppercase mb-2">
                    Sources ({searchResults.sources.length})
                  </h4>
                  <div className="space-y-1.5">
                    {searchResults.sources.map((source, index) => (
                      <div key={`${source.documentId}-${source.chunkIndex}`} className="p-2 bg-slate-50 rounded-lg border border-[#e2e8f0]">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] font-bold text-primary">#{index + 1}</span>
                          <span className="text-[10px] font-semibold text-[#052831]">{source.fileName}</span>
                          <span className="ml-auto text-[9px] font-bold text-[#64748b]">
                            {Math.round(source.relevanceScore * 100)}%
                          </span>
                        </div>
                        <p className="text-[10px] text-[#64748b] leading-relaxed line-clamp-2">{source.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-[#e2e8f0] my-4" />

          {/* Test Section */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FlaskConical size={16} className="text-purple-500" />
              <h4 className="font-bold text-[#052831] text-sm">Test Knowledge Base</h4>
              {testResults && (
                <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                  testResults.status === 'passed'
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : testResults.status === 'partial'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : testResults.status === 'failed'
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : 'bg-slate-50 text-slate-500 border-slate-200'
                }`}>
                  {testResults.status === 'passed' && <><CheckCircle2 size={10} /> All Passed</>}
                  {testResults.status === 'partial' && <><Clock size={10} /> Partial</>}
                  {testResults.status === 'failed' && <><XCircle size={10} /> Failed</>}
                  {testResults.status === 'no_documents' && 'No Documents'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {testResults && (
                <button
                  onClick={() => setTestExpanded(!testExpanded)}
                  className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors"
                >
                  {testExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {testExpanded ? 'Collapse' : 'Expand'}
                </button>
              )}
              <button
                onClick={handleTest}
                disabled={testMutation.isPending || !stats || stats.documentsProcessed === 0}
                className="px-3 py-1.5 bg-purple-500 text-white text-xs font-bold rounded-lg hover:bg-purple-600 transition-all disabled:opacity-50 flex items-center gap-1.5"
              >
                {testMutation.isPending ? (
                  <><Loader2 size={12} className="animate-spin" /> Running Tests...</>
                ) : (
                  <><FlaskConical size={12} /> Run Tests</>
                )}
              </button>
            </div>
          </div>

          {/* Test Results */}
          {testExpanded && (
            <div className="mt-3 space-y-3 overflow-y-auto max-h-[280px]">
              {testMutation.isPending && !testResults && (
                <div className="flex items-center justify-center py-6 text-slate-400 text-xs">
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Running automated test queries... this may take a minute.
                </div>
              )}

              {testResults && testResults.status !== 'no_documents' && (
                <>
                  {/* Summary Bar */}
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-[#052831]">{testResults.summary.passed}/{testResults.summary.total}</p>
                      <p className="text-[9px] text-slate-500 uppercase font-medium">Passed</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-[#052831]">{Math.round(testResults.summary.avgRelevance * 100)}%</p>
                      <p className="text-[9px] text-slate-500 uppercase font-medium">Avg Relevance</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-[#052831]">{testResults.summary.avgLatencyMs}ms</p>
                      <p className="text-[9px] text-slate-500 uppercase font-medium">Avg Latency</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-[#052831]">{testResults.summary.documentsIndexed}</p>
                      <p className="text-[9px] text-slate-500 uppercase font-medium">Docs Indexed</p>
                    </div>
                  </div>

                  {/* Individual Results */}
                  <div className="space-y-2">
                    {testResults.results.map((result) => (
                      <div
                        key={result.query}
                        className={`p-3 rounded-lg border ${
                          result.passed
                            ? 'bg-green-50/50 border-green-100'
                            : 'bg-red-50/50 border-red-100'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {result.passed ? (
                            <CheckCircle2 size={14} className="text-green-500 mt-0.5 shrink-0" />
                          ) : (
                            <XCircle size={14} className="text-red-500 mt-0.5 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-semibold text-[#052831]">{result.description}</span>
                              <span className="ml-auto text-[9px] text-slate-400 shrink-0">{result.latencyMs}ms</span>
                            </div>
                            <p className="text-[10px] text-[#64748b] italic mb-1">"{result.query}"</p>
                            <div className="flex items-center gap-3 text-[9px] text-slate-500">
                              <span>{result.sourcesFound} sources</span>
                              <span>Relevance: {Math.round(result.avgRelevance * 100)}%</span>
                              {result.topSource && (
                                <span className="truncate">Top: {result.topSource.fileName}</span>
                              )}
                            </div>
                            {result.answer && (
                              <p className="text-[10px] text-[#052831] mt-1.5 bg-white/60 rounded p-1.5 line-clamp-2">{result.answer}</p>
                            )}
                            {result.error && (
                              <p className="text-[10px] text-red-600 mt-1">Error: {result.error}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Search / Filter bar */}
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <Search size={18} className="text-slate-400 group-focus-within:text-primary transition-colors" />
        </div>
        <input
          type="text"
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          className="block w-full pl-11 pr-4 py-3 bg-white border border-[#e2e8f0] rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-[#052831] placeholder-slate-400 text-sm"
          placeholder="Search documents by name..."
        />
      </div>

      {/* Document Table */}
      <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-[#64748b] text-sm">
            <Loader2 size={20} className="animate-spin mr-2" /> Loading documents...
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="py-12 text-center">
            <Upload size={32} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-[#64748b]">
              {filterQuery ? 'No documents match your search.' : 'No documents yet. Upload your first document to get started.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-[#e2e8f0]">
                <th className="px-6 py-4 text-xs font-bold text-[#64748b] uppercase tracking-wider">Document Name</th>
                <th className="px-6 py-4 text-xs font-bold text-[#64748b] uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-[#64748b] uppercase tracking-wider hidden md:table-cell">Upload Date</th>
                <th className="px-6 py-4 text-xs font-bold text-[#64748b] uppercase tracking-wider hidden md:table-cell">Size</th>
                <th className="px-6 py-4 text-xs font-bold text-[#64748b] uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {filteredDocs.map((doc: AvatarKBDocument) => {
                const ext = getFileExtension(doc.fileName);
                const typeStyle = FILE_TYPE_STYLES[ext] || FILE_TYPE_STYLES.txt;
                const statusStyle = STATUS_STYLES[doc.processingStatus] || STATUS_STYLES.pending;
                return (
                  <tr key={doc.documentId} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`flex items-center justify-center size-9 ${typeStyle.bg} ${typeStyle.text} rounded-lg border ${typeStyle.border}`}>
                          <span className="text-[10px] font-black uppercase">{ext}</span>
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-semibold text-[#052831] truncate block">{doc.fileName}</span>
                          {doc.processingStatus === 'completed' && doc.chunkCount > 0 && (
                            <span className="text-[10px] text-[#64748b]">{doc.chunkCount} chunks</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${statusStyle.bg} ${statusStyle.text}`}>
                        <span className={`size-1.5 rounded-full ${statusStyle.dot} ${statusStyle.dotAnimate ? 'animate-pulse' : ''}`} />
                        {doc.processingStatus === 'completed' ? 'Processed' : doc.processingStatus.charAt(0).toUpperCase() + doc.processingStatus.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748b] hidden md:table-cell">
                      {formatDate(doc.uploadedAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748b] hidden md:table-cell">
                      {formatFileSize(doc.fileSizeBytes)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1 text-slate-400">
                        <button
                          onClick={() => handleDelete(doc.documentId, doc.fileName)}
                          disabled={deleteMutation.isPending}
                          className="hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                          title="Delete document"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Document count */}
      {!isLoading && filteredDocs.length > 0 && (
        <div className="flex items-center justify-between text-sm text-[#64748b] px-2">
          <p>Showing {filteredDocs.length} of {documents?.length || 0} documents</p>
          {stats && (
            <div className="flex items-center gap-4 text-xs">
              <span>{stats.totalChunks} chunks</span>
              <span>{formatFileSize(stats.totalSizeBytes)} total</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
